import React, { useState, useEffect } from "react";
import { Card, Accordion } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import { Highlighter } from "react-bootstrap-typeahead";
import errorToast from "helpers/errorToast";
import { getCurrencies } from "api";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import InputField from "components/atoms/InputField/InputField";
import { FormStepTypes } from "constants/";
import FormItemInfo from "../Common/FormItemInfo";
import SearchSelect from "../Common/SearchSelect";
import { ICurrencyOption, IDefaultCurrencyOption } from "../PackingList/Types";

interface ICurrency {
  id: number;
  label: string;
  code: string;
  value: string;
}

interface IQuotationProps {
  offPlatformOptionalFields?: boolean;
}

function QuotationForm(props: IQuotationProps) {
  const { offPlatformOptionalFields } = props;
  const methods = useFormContext();
  const {
    setValue,
    formState: { errors },
    getValues,
  } = methods;
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const [currencyOption, setCurrencyOption] = useState<IDefaultCurrencyOption[]>([]);

  const shipmentErrors = () => {
    if (errors.quotation?.price || errors.quotation?.currency_code || errors.quotation?.chargeable_weight) return true;
    return false;
  };

  const handleContactRenderOption = ({ option, text }: { option: ICurrencyOption; text: string }) => (
    <div className="p-2">
      <div className="font-weight-bold font-12">
        <Highlighter search={text}>{`(${option.code}) ${option.label}`}</Highlighter>
      </div>
    </div>
  );

  const handleCurrencyChange = (currency: ICurrency[]) => {
    setValue("quotation.currency_code", currency[0].code);
  };

  useEffect(() => {
    getCurrencies()
      .then((res) => {
        if (res.data) setCurrencyOption(res.data);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.packing-list.couldNotRetrieveCurrencies",
            defaultMessage: "Could not retrieve currencies. Please try again later.",
          }),
        ),
      );
  }, []);

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.QUOTATION}>
        <div className="d-flex align-items-center">
          <div>
            {shipmentErrors() ? (
              <ExclamationIcon className="mr-2" />
            ) : !errors.quotation &&
              getValues("quotation.chargeable_weight") &&
              getValues("quotation.currency_code") &&
              getValues("quotation.price") ? (
              <CheckIcon className="mr-2" />
            ) : null}
          </div>
          <h5 className="d-flex align-items-center">
            <FormattedMessage id="quote.listing.quotation" defaultMessage="Quotation" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.CONFIRMATION ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.QUOTATION}>
        <Card.Body>
          <div className="flex-row">
            <div className="w-200">
              <FormItemInfo
                labelId="quote.listing.quotationDetail"
                label="Quotation details"
                descId="shipments.provideShipmentsDetails"
                description="Provide your shipment info"
              />
            </div>
            <div className="w-192">
              <SearchSelect
                name="quotation.currency_code"
                label={intl.formatMessage({ id: "general.currency", defaultMessage: "Currency" })}
                options={
                  currencyOption?.length > 0
                    ? currencyOption.map((currency: IDefaultCurrencyOption) => ({
                        id: currency.id,
                        label: currency.name,
                        code: currency.code,
                        value: currency.code,
                      }))
                    : []
                }
                filterBy={["code", "label"]}
                placeholder={intl.formatMessage({ id: "general.currency", defaultMessage: "Currency" })}
                handleRenderOption={handleContactRenderOption}
                required={offPlatformOptionalFields}
                onSelect={handleCurrencyChange}
              />
            </div>
            <div className="w-192">
              <InputField
                label={intl.formatMessage({
                  id: "shipment-quotes.components.bookQuoteDialog.price",
                  defaultMessage: "Price",
                })}
                type="textinput"
                field_id="quotation.price"
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                }}
                required={offPlatformOptionalFields}
              />
            </div>
            <div className="w-192">
              <InputField
                label={intl.formatMessage({
                  id: "quote-request.costDetailChargeableWeight",
                  defaultMessage: "Chargeable weight",
                })}
                type="textinput"
                field_id="quotation.chargeable_weight"
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                }}
                required={offPlatformOptionalFields}
              />
            </div>
          </div>
          <div className="flex-row mt-3">
            <div className="w-200" />
            <div className="w-192">
              <InputField
                label={intl.formatMessage({
                  id: "shipments.weight",
                  defaultMessage: "Weight (Kg)",
                })}
                type="textinput"
                field_id="quotation.weight"
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                }}
              />
            </div>
            <div className="w-192">
              <label>
                <FormattedMessage id="shipments.Volume" defaultMessage="Volume m" />
                <sup>3</sup>
              </label>
              <InputField
                type="textinput"
                field_id="quotation.volume"
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                }}
              />
            </div>
          </div>
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

export default QuotationForm;
QuotationForm.propTypes = {
  offPlatformOptionalFields: PropTypes.bool,
};

QuotationForm.defaultProps = {
  offPlatformOptionalFields: false,
};
